import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import Sidebar from "./Sidebar.jsx";
import TopBar from "./TopBar.jsx";
import SpodniLista from "./SpodniLista.jsx";
import SystemAlertBanner from "../components/SystemAlertBanner.jsx";
import { SlackPruh } from "../components/SlackStatus.jsx";
import ToastHost from "../components/Toast.jsx";
import ConfirmHost from "../components/confirmHost.jsx";
import { AlertProvider } from "../lib/AlertContext.jsx";
import { touchSession, getCurrentUser } from "../lib/auth.js";
import { cestyProRoli } from "../lib/menu.js";
import { prewarmClientsList } from "../lib/clientsCache.js";
import { nactiTaby, pridej, zavri, prepis, presun, UDALOST, KLIC, KLIC_AKTIVNI, MAX_TABU, noveId } from "../lib/taby.js";
import { toast } from "../components/Toast.jsx";

// Sekce, do kterých má role "manager" přístup — musí odpovídat jeho menu
// (Sidebar) a routám v App.jsx. 17.7.2026: odemčeny všechny manažerské moduly
// (Reporting, Trenéři, Clients management, HR, Operations Úkoly + Time tracking,
// Deník, Systém). Owner v náhledu "jako manažer" se chová stejně.
// Detailní oprávnění dál hlídá RequireAuth per routa + backend role guardy.
const MANAGER_ALLOWED_PREFIXES = [
  "/manager", "/hr", "/operations/ukoly", "/operations/meetingy",
  // 22.9.2026: bez "/app-reporting", manažer analytiku nevidí (Láďa).
  "/dashboard", "/weekly", "/monthly", "/yearly", "/stats", "/reporting", "/marketing",
  "/clients", "/log-cash", "/denik", "/dokumenty", "/moje-vyplata", "/bootcampy",
  "/sales", "/automatizace", "/audit-log", "/feedback", "/sms-sablony",
  // 1. 10. 2026: Rezervace (seznam a kalendář týmu), bez toho se manažer vracel na dashboard.
  "/rezervace",
];

/** Bezpečné čtení a zápis do localStorage — v soukromém okně prostě nic. */
const cti = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const zapis = (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* nevadí */ } };

// Stránky, kde poslední kus adresy nedává čitelný název (audit 22. 9. 2026).
const NAZVY_ZALOZEK = {
  "/analytika/prehled": "Analytika", "/analytika/produkty": "Produkty a stránky",
  "/analytika/reklamy": "Reklamy", "/analytika/reporty": "Pondělní reporty",
  "/web/analytika": "Web podrobně", "/app-reporting": "Klientská aplikace",
};

/** Název záložky podle adresy. Detail v query parametru si název dodá sám. */
function nazevStranky(cesta) {
  const bez = cesta.split("?")[0].replace(/\/$/, "");
  if (NAZVY_ZALOZEK[bez]) return NAZVY_ZALOZEK[bez];
  const posledni = bez.split("/").filter(Boolean).pop() || "Přehled";
  return posledni.charAt(0).toUpperCase() + posledni.slice(1).replace(/-/g, " ");
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Schované menu na počítači: kdo chce mít práci přes celou obrazovku,
  // odsune panel a jedním klepnutím ho vrátí. Pamatuje se to i po zavření.
  const [menuSkryte, setMenuSkryteStav] = useState(() => cti("fw40.menu.skryte") === "1");
  const setMenuSkryte = (v) => { setMenuSkryteStav(v); zapis("fw40.menu.skryte", v ? "1" : ""); };
  const location = useLocation();
  const navigate = useNavigate();

  // Záložky uvnitř adminu. Úkol nebo detail klienta se dá nechat otevřený
  // a přeskakovat mezi nimi bez druhého okna prohlížeče.
  const [taby, setTabyStav] = useState(() => nactiTaby(cti(KLIC)));
  // Id zalozky, nad kterou se prave tahne jina — zvyrazni se jako cil (13.8.2026).
  const [dragCil, setDragCil] = useState(null);
  const [aktivni, setAktivniStav] = useState(() => cti(KLIC_AKTIVNI) || null);
  const setTaby = (t) => { setTabyStav(t); zapis(KLIC, JSON.stringify(t)); };
  const setAktivni = (id) => { setAktivniStav(id); zapis(KLIC_AKTIVNI, id || ""); };
  const cesta = location.pathname + location.search;
  const hlavickaRef = useRef(null);
  const listaRef = useRef(null);
  const aktivniRef = useRef(aktivni);
  const cestaRef = useRef(cesta);
  aktivniRef.current = aktivni;
  cestaRef.current = cesta;

  // Kolik nahoře zabírá lišta a řada záložek. Detail úkolu na celé stránce se
  // podle toho posadí pod ně, aby zůstaly vidět a dalo se přepínat.
  useLayoutEffect(() => {
    const zmer = () => {
      const v = hlavickaRef.current?.offsetHeight || 0;
      document.documentElement.style.setProperty("--admin-top", `${v}px`);
      // Samotná horní lišta bez řady záložek. Řada se pod ni lepí, takže
      // nesmí použít --admin-top, ta obsahuje i ji samotnou.
      const l = listaRef.current?.offsetHeight || 0;
      if (l) document.documentElement.style.setProperty("--admin-lista", `${l}px`);
    };
    zmer();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(zmer) : null;
    if (ro && hlavickaRef.current) ro.observe(hlavickaRef.current);
    window.addEventListener("resize", zmer);
    return () => { ro?.disconnect(); window.removeEventListener("resize", zmer); };
  }, [taby.length]);

  // Chození po adminu přepisuje otevřenou záložku, stejně jako v prohlížeči.
  useEffect(() => {
    setTabyStav((stav) => {
      if (!stav.length) return stav;
      const tab = stav.find((t) => t.id === aktivniRef.current);
      if (!tab || tab.to === cesta) return stav;
      // Zavření detailu vrací na seznam, který už otevřený je. Dvě stejné
      // záložky vedle sebe nedávají smysl, tak se ta odcházející zavře.
      const uz = stav.find((t) => t.to === cesta && t.id !== aktivniRef.current);
      if (uz) {
        const zbytek = stav.filter((t) => t.id !== aktivniRef.current);
        zapis(KLIC, JSON.stringify(zbytek));
        setAktivniStav(uz.id);
        zapis(KLIC_AKTIVNI, uz.id);
        return zbytek;
      }
      const dalsi = prepis(stav, aktivniRef.current, { to: cesta, title: nazevStranky(cesta) });
      zapis(KLIC, JSON.stringify(dalsi));
      return dalsi;
    });
  }, [cesta]);

  // Novou záložku si stránky vyžádají událostí, ať se kvůli tomu nemusí
  // protahovat funkce přes celý admin.
  useEffect(() => {
    const nova = (e) => {
      const to = e.detail?.to;
      if (!to) return;
      setTabyStav((stav) => {
        const zaklad = stav.length ? stav : [{ id: noveId(), to: cestaRef.current, title: nazevStranky(cestaRef.current) }];
        const { taby: dalsi, id, plno } = pridej(zaklad, { to, title: e.detail?.title });
        if (plno) {
          toast(`Víc než ${MAX_TABU} záložek už je nepřehledné, něco zavři.`, "error");
          return stav;
        }
        zapis(KLIC, JSON.stringify(dalsi));
        if (id) {
          setAktivniStav(id);
          zapis(KLIC_AKTIVNI, id);
          navigate(to);
        }
        return dalsi;
      });
    };
    window.addEventListener(UDALOST, nova);
    return () => window.removeEventListener(UDALOST, nova);
  }, [navigate]);

  // Manažer smí jen do povolených sekcí. Pokus o cizí URL → přesměruj na /manager.
  useEffect(() => {
    const u = getCurrentUser();
    if (u?.role === "manager") {
      // 21. 10. 2026: povolené je i všechno, co má manažer v menu. Seznam výše se
      // ručně nedoplňoval, takže Kniha úrazů a Moje školení v menu svítily, ale
      // klik vrátil manažera na Dashboard. Menu je teď zdroj pravdy, seznam je doplněk.
      const zMenu = cestyProRoli("manager");
      const allowed = [...MANAGER_ALLOWED_PREFIXES, ...zMenu].some((p) => p && location.pathname.startsWith(p));
      if (!allowed) navigate("/manager/dashboard", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Jednorázově: vypnout obnovu scroll pozice prohlížečem + scroll anchoring.
  // Scroll anchoring je hlavní příčina "otevře se v půlce" — když cílová stránka po
  // přechodu doasynchronně vloží obsah nahoru, prohlížeč ukotví pohled a posune scroll dolů.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    document.documentElement.style.overflowAnchor = "none";
    document.body.style.overflowAnchor = "none";
  }, []);
  // Při KAŽDÉM přepnutí stránky odscrolluj úplně na vrch. Vícekrát (před vykreslením +
  // po rAF + se zpožděním), aby to chytlo i posun po donačtení dat z API.
  useLayoutEffect(() => {
    const toTop = () => {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const main = document.querySelector("main");
      if (main) main.scrollTop = 0;
    };
    toTop();
    const r = requestAnimationFrame(toTop);
    const t1 = setTimeout(toTop, 80);
    const t2 = setTimeout(toTop, 250);
    const t3 = setTimeout(toTop, 600);
    return () => { cancelAnimationFrame(r); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [location.pathname]);

  // Pre-warm seznam klientu (jmena pro ClientAutocomplete v Log ST/Cash/Expired modalech).
  // Bezi na pozadi hned po loginu → kdyz user dialog otevre, cache je hotova.
  useEffect(() => {
    prewarmClientsList();
  }, []);

  // Sliding window pro session — každé otevření aplikace + každou hodinu posune expiraci o 48 h.
  // Uživatel se odhlásí pouze pokud aplikaci 48 h neotevře.
  useEffect(() => {
    touchSession();
    const id = setInterval(touchSession, 3600 * 1000); // jednou za hodinu
    // Plus posunout expiraci při interakci (klik, scroll) — debounced
    let debounceTimer = null;
    const onActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        touchSession();
        debounceTimer = null;
      }, 30 * 1000); // max jednou za 30 s
    };
    window.addEventListener("click", onActivity);
    window.addEventListener("keydown", onActivity);
    return () => {
      clearInterval(id);
      window.removeEventListener("click", onActivity);
      window.removeEventListener("keydown", onActivity);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  return (
    <AlertProvider>
      <div className="flex bg-brand-cream min-h-screen font-sans">
        {/* Na telefonu se panel otevírá jako zásuvka, tam schovávání neplatí. */}
        <div className={menuSkryte ? "lg:hidden" : ""}>
          <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
        </div>
        <main className="flex-1 min-w-0">
          <div ref={hlavickaRef} className="sticky top-0 z-30">
          {/* 3.9.2026: relative z-20 kvůli tomu, že horní lišta má backdrop-blur,
              a tím vytváří vlastní vrstvu. Bez toho řada záložek (z-10) překreslila
              rozbalenou nabídku Náhled jako, která je uvnitř lišty. */}
          <div ref={listaRef} className="relative z-20">
            <TopBar setSidebarOpen={setSidebarOpen} menuSkryte={menuSkryte} setMenuSkryte={setMenuSkryte} />
          </div>

          {/* Řada záložek. Ukazuje se, až když je otevřená víc než jedna;
              na telefonu nikdy, tam by ukrojila půl obrazovky. */}
          {taby.length > 1 && (
            <div className="hidden lg:flex items-center gap-1 overflow-x-auto border-b border-slate-200 bg-white/90 px-4 sm:px-6 py-1.5 sticky top-[var(--admin-lista,57px)] z-10 backdrop-blur">
              {taby.map((t) => {
                const jeAktivni = t.id === aktivni;
                return (
                  <div
                    key={t.id}
                    data-zalozka={t.to}
                    /* Poradi zalozek jde zmenit pretazenim (13.8.2026, prani Ladi). */
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => { e.preventDefault(); if (dragCil !== t.id) setDragCil(t.id); }}
                    onDragLeave={() => setDragCil((d) => (d === t.id ? null : d))}
                    onDragEnd={() => setDragCil(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragCil(null);
                      const id = e.dataTransfer.getData("text/plain");
                      if (id && id !== t.id) setTaby(presun(taby, id, t.id));
                    }}
                    className={`group flex min-w-0 max-w-[16rem] shrink-0 cursor-grab active:cursor-grabbing items-center gap-1 rounded-xl border px-2 py-1 text-sm transition ${
                      dragCil === t.id
                        ? "border-brand ring-2 ring-brand/30 bg-brand-50 text-brand-700"
                        : jeAktivni ? "border-brand/40 bg-brand-50 text-brand-700" : "border-transparent text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => { setAktivni(t.id); navigate(t.to); }}
                      className="min-w-0 flex-1 truncate text-left font-semibold"
                      title={t.title}
                    >
                      {t.title}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const { taby: dalsi, aktivni: novyAktivni, kam } = zavri(taby, t.id, aktivni);
                        setTaby(dalsi);
                        setAktivni(dalsi.length > 1 ? novyAktivni : null);
                        if (kam) navigate(kam);
                      }}
                      className="rounded p-0.5 text-slate-400 opacity-60 transition hover:bg-white hover:text-rose-700 group-hover:opacity-100"
                      aria-label={`Zavřít záložku ${t.title}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          </div>
          {/* 21.9.2026: výzva k propojení Slacku (status 🌴 při dovolené), dokud ho člověk nepropojí. */}
          <SlackPruh />
          {/* SystemAlertBanner se zobrazí jen owner/manager pokud jsou aktivní system errors. */}
          <SystemAlertBanner />
      <ToastHost />
      <ConfirmHost />
          <Outlet />
          {/* Rezerva pod obsahem, aby poslední řádek nezmizel pod spodní lištou.
              Na počítači lišta není, tam se místo nekrade. */}
          <div className="safe-bottom-space lg:hidden" aria-hidden="true" />
        </main>
        {/* Spodní lišta na telefonu pro všechny role (2.9.2026). Nahrazuje
            hamburger se zásuvkou i vlastní lištu trenérského portálu. */}
        <SpodniLista />
      </div>
    </AlertProvider>
  );
}
